import { useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { useProfile } from "../lib/queries";
import { useLocalImage } from "../lib/localImage";

/** The picture to show: the one waiting to upload first, then the synced one; null for none. */
export function useAvatarSrc(): string | null {
  const { data } = useProfile();
  const local = useLocalImage(data?.localAvatarKey);
  return local ?? data?.avatarUrl ?? null;
}

/**
 * A profile picture in a circle: the user's own, or someone else's with `src` (null: none). Decorative
 * (the name or label sits next to it). Without a picture: `fallback`, or a person glyph on the fill colour.
 */
export function Avatar({ size, className = "", fallback, src: other }: { size: number; className?: string; fallback?: ReactNode; src?: string | null }) {
  const own = useAvatarSrc();
  const src = other === undefined ? own : other;
  const [broken, setBroken] = useState<string | null>(null);
  const style = { width: size, height: size };
  if (!src || broken === src) {
    return (
      fallback ?? (
        <span className={`inline-flex shrink-0 items-center justify-center rounded-full bg-fill text-label-2 ${className}`} style={style} aria-hidden>
          <Icon name="person" size={Math.round(size * 0.5)} weight="fill" />
        </span>
      )
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      decoding="async"
      draggable={false}
      onError={() => setBroken(src)}
      className={`shrink-0 rounded-full bg-fill object-cover ${className}`}
      style={style}
      data-testid="avatar-img"
    />
  );
}
